/* eslint-disable no-empty-pattern */

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'isomorphic-dompurify';
import { useMediaQuery } from 'react-responsive';
import { vehicleContext } from '../VehicleProvider';
import { useCTATypes } from '../../hooks/useCTATypes';
import { VehicleDetails } from '../../types';
import Spinner from '../Spinner';
import FirstButton from './FirstButton';
import SecondButton from './SecondButton';
import {
  Container,
  ButtonsContainer,
  CopyNotes,
  HowEDepositWorks,
  CopyNotesLaptopWrapper,
} from './CTASection.styles';
import 'react-loading-skeleton/dist/skeleton.css';
import { CTAContext } from '../CTAProvider';
import InterstitialModal from '../InterstitialModal';
import Modal from '../Modal';
import { vdpPageLoad } from '../../utils/analyticsEventListeners';

interface CTASectionProps {
  isSticky?: boolean;
}

// This Component is used only for Tier 1
const CTASection: React.FC<CTASectionProps> = ({ isSticky }) => {
  const isLaptop = useMediaQuery({ minWidth: 1024 });
  const { t } = useTranslation();

  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const onModalOpen = useCallback(() => {
    setModalOpen(true);
  }, []);

  const onModalClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  const {
    mutate,
    bookATestDriveUrl,
    requestAQuoteUrl,
    setMutate,
    redirect,
    contactDealerUrl,
    howEDepositWorksUrl,
    paymentCalculatorUrl,
  } = useContext(CTAContext);

  const openHowEDepositWorks = useCallback(
    (event: any) => {
      const { target } = event;
      if (target.classList.contains('clickable') || target.tagName === 'BUTTON') {
        if (howEDepositWorksUrl) {
          window.location.href = howEDepositWorksUrl;
        }
      }
    },
    [howEDepositWorksUrl],
  );

  const { vehicle } = useContext(vehicleContext);
  const { weblink, isAvailableNow, dealerSearchInfo, saleOrderType } =
    (vehicle as VehicleDetails) ?? {};

  // const nonAudi = vehicle?.vehicleType === 'non-audi';
  const isSold = saleOrderType?.includes('SOLD');

  const isAtYourDoor = dealerSearchInfo?.services?.includes('atyourdoor') ?? false;

  // const showPlaceEDepositButton =
  //   status === 'UNALLOCATED' ||
  //   (status === 'ALLOCATED' && activeOnlineSaleStatus === 'DEPOSIT_PENDING');
  // const isPipeline = status === 'UNALLOCATED' || status === 'ALLOCATED';

  // const showCongratulationsText =
  //   (activeOnlineSaleId !== null &&
  //     activeOnlineSaleId !== undefined &&
  //     activeOnlineSaleId === activeOnlineSale) ||
  //   activeOnlineSaleStatus === 'DEPOSIT_PLACED';

  // const showOnlineShoppingCTAS = onlineSaleEligibility?.includes('ONLINE_SHOPPING') ?? false;

  const { firstButtonType, secondButtonType, legalTextType } = useCTATypes({
    isAtYourDoor,
  });

  const legalNote = useMemo(() => {
    // for Place e-deposit button
    if (legalTextType === 'PLACE_E_DEPOSIT') {
      const placeEDepositText = isSticky
        ? t('vehiclePricingBar.dealerListPrice.depositLegalText')
        : `${t('vehiclePricingBar.dealerListPrice.depositLegalText')} ${t(
            'vehiclePricingBar.dealerListPrice.explore',
          )} <button class='clickable'>${t(
            'vehiclePricingBar.dealerListPrice.howEDeposit',
          )}</button>`;
      const sanitizedHTML = DOMPurify.sanitize(placeEDepositText);
      // eslint-disable-next-line react/no-danger
      return (
        <HowEDepositWorks
          onClick={openHowEDepositWorks}
          dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
        />
      );
    }

    // for Reserved button
    if (legalTextType === 'VEHICLE_RESERVED')
      return t('vehiclePricingBar.dealerListPrice.vehicleReserved');

    if (legalTextType === 'VEHICLE_RESERVED_CONGRATS')
      return t('vehiclePricingBar.dealerListPrice.vehicleReservedCongrats');

    return null;
  }, [isSticky, legalTextType, openHowEDepositWorks, t]);

  // const eDepositExceeded = createSaleErrorMessage === 'E_DEPOSIT_EXCEEDED_ATTEMPTS';

  // const errorNote = useMemo(() => {
  //   if (eDepositExceeded) return t('vehiclePricingBar.dealerListPrice.exceededEdepositError');

  //   return null;
  // }, [eDepositExceeded, t]);

  // useEffect(() => {
  //   if (eDepositExceeded) {
  //     setMutate(false);
  //     setModalOpen(false);
  //   }
  // }, [setMutate, eDepositExceeded]);

  // useEffect(() => {
  //   if (loadingSale) return;
  //   if (showOnlineShoppingCTAS && vehicle) vdpPageLoad(vehicle, true);
  //   if (!showOnlineShoppingCTAS && vehicle) vdpPageLoad(vehicle, false);
  // }, [showOnlineShoppingCTAS, vehicle, loadingSale]);

  if (!vehicle) return null;

  // if (status === 'loading' || loadingSale || mutate)
  //   return (
  //     <Container data-testid="cta-container">
  //       <Spinner />
  //     </Container>
  //   );

  // TODO: need to update the color props, currently not passed!
  return (
    <>
      <Container data-testid="cta-container">
        <ButtonsContainer sticky={isSticky}>
          <SecondButton
            weblink={weblink}
            isAvailableNow={isAvailableNow}
            isSticky={isSticky}
            secondButtonType={secondButtonType}
            redirect={redirect}
            contactDealerUrl={contactDealerUrl}
            requestAQuoteUrl={requestAQuoteUrl}
            onModalOpen={onModalOpen}
          />
          <FirstButton
            weblink={weblink}
            isSticky={isSticky}
            firstButtonType={firstButtonType}
            bookATestDriveUrl={bookATestDriveUrl}
            requestAQuoteUrl={requestAQuoteUrl}
            howEDepositWorksUrl={howEDepositWorksUrl}
            paymentCalculatorUrl={paymentCalculatorUrl}
          />
          {legalNote && isLaptop && !isSticky && (
            <CopyNotesLaptopWrapper>
              <CopyNotes sticky={isSticky}>{legalNote}</CopyNotes>
            </CopyNotesLaptopWrapper>
          )}
        </ButtonsContainer>
        {legalNote && (isSticky || !isLaptop) && (
          <CopyNotes sticky={isSticky}>{legalNote}</CopyNotes>
        )}
      </Container>

      {modalOpen && (
        <Modal eDeposit onClose={onModalClose} modalOpen={modalOpen}>
          <InterstitialModal setMutate={setMutate} />
        </Modal>
      )}
    </>
  );
};

export default CTASection;
